import { memo, useCallback, useRef } from 'react';
import type { FormikHelpers } from 'formik';
import { useTypedSelector } from 'hooks';
import type { ResourceResponse } from 'types';
import { route, when } from 'util/support';
import { isFormChanged } from 'util/forms';
import type { IbanXsBanksCountriesPivot } from 'features/openBanking/types';
import { selectBanksFlowsOptions, selectConstants, selectIsConstantsLoading } from 'features/general/selectors';
import { useIbanXSBanksCountriesPivotActions } from 'features/openBanking/actions';
import ActionPanel, { type ActionPanelProps } from 'ui/molecules/ActionPanel';
import When from 'ui/support/When';
import {
    useResourceContext,
    ResourceDetail,
    type ResourceDetailProps
} from "ui/widgets/Resource";
import { IbanXSBanksCountriesPivot } from 'ui/forms/OpenBanking';

const IbanXSBanksCountriesPivotDetail = (props: ResourceDetailProps) => {
    const { resource, onClose } = useResourceContext<IbanXsBanksCountriesPivot>();

    const {
        updateIbanXSBanksCountriesPivot
    } = useIbanXSBanksCountriesPivotActions();

    const helpers = {
        resource,
        onClose,
        updateIbanXSBanksCountriesPivot
    };
    const helpersRef = useRef(helpers);
    helpersRef.current = helpers;

    const onSuccess = useCallback((formHelpers?: FormikHelpers<
        Partial<IbanXSBanksCountriesPivot.IbanXSBanksCountriesPivot>
    >) => {
        helpersRef.current.onClose();
        formHelpers!.resetForm();
    }, []);

    return (
        <ResourceDetail
            {...props}
            disableCloseOnBackdropClick
            onClose={onClose}
        >
            <When
                if={Boolean(resource)}
                then={useCallback(() => {
                    const {
                        resource,
                        updateIbanXSBanksCountriesPivot
                    } = helpersRef.current;

                    return (
                        <IbanXSBanksCountriesPivotForm
                            resource={resource}
                            requestHandler={updateIbanXSBanksCountriesPivot}
                            onSuccess={onSuccess}
                        />
                    );
                }, [onSuccess])}
            />
        </ResourceDetail>
    );
};

export default memo(IbanXSBanksCountriesPivotDetail);

export function IbanXSBanksCountriesPivotForm({
    resource,
    requestHandler,
    onSuccess,
    SaveActionProps
}: {
    readonly resource: Partial<IbanXsBanksCountriesPivot>;
    readonly requestHandler: (params: Partial<IbanXsBanksCountriesPivot>) => any,
    readonly onSuccess?: (formHelpers?: FormikHelpers<
        Partial<IbanXSBanksCountriesPivot.IbanXSBanksCountriesPivot>
    >) => void;
    readonly SaveActionProps?: ActionPanelProps['SaveActionProps'];
}) {
    const isConstantsLoading = useTypedSelector(selectIsConstantsLoading);
    const bootstrapData = useTypedSelector(selectConstants);

    const { onClose } = useResourceContext<IbanXsBanksCountriesPivot>();

    const formBootstrapData = {
        OpenBankingLogosDomain: bootstrapData.OpenBankingLogosDomain,
        BanksFlows: useTypedSelector(selectBanksFlowsOptions)
    };

    const formProps: IbanXSBanksCountriesPivot.IbanXSBanksCountriesPivotForm = {
        isBootstrapDataLoading: (
            isConstantsLoading
        ),
        enableReinitialize: false,
        bootstrapData: formBootstrapData,
        initialValues: IbanXSBanksCountriesPivot.getInitialValues(resource, formBootstrapData),
        onSaveOrCreate: async ({
            flow,
            options,
            logoUrl,
            iso2 = [],
            ...values
        }, formHelpers) => {
            const [request, result] = requestHandler({
                ...values,
                logoUrl: when(
                    logoUrl,
                    route([logoUrl]),
                    null
                ),
                options: JSON.stringify(options),
                flow: when(flow, () => flow!.id, flow),
                iso2: iso2.at(0)
            });

            const response = (await request) as ResourceResponse<IbanXsBanksCountriesPivot>;

            when(response.success, () => onSuccess?.(formHelpers));

            return result;
        }
    };

    const form = IbanXSBanksCountriesPivot.useForm(formProps);

    return (
        <IbanXSBanksCountriesPivot.Form
            {...form}
            {...formProps}
        >
            <ActionPanel
                isLoading={form.isSubmitting}
                onSave={async () => { }}
                onCancel={onClose}
                SaveActionProps={{
                    type: 'submit',
                    disabled: !form.isValid || !isFormChanged(form),
                    loading: form.isSubmitting,
                    ...SaveActionProps
                }}
            />
        </IbanXSBanksCountriesPivot.Form>
    );
}
